import { Err, Ok, Result } from 'ts-results'

import { DataError } from 'app/common/application'

import {
  AvailableConnection,
  AvailableOrInstalledConnection,
  ConnectionConfig,
  ConnectionCreateRequest,
  ConnectionCreateResponse,
  ConnectionEntity,
  ConnectionUpdateRequest,
  ConnectionUpdateResponse,
  InstalledConnection,
  isInstalledConnection,
} from '../types'
import { AmazonConfig, AmazonFinishAuth, AmazonUpdateConnection } from '../types/amazon'
import { CriteoConfig, CriteoFinishAuth, CriteoUpdateConnection } from '../types/criteo'
import { DV360Config, DV360FinishAuth, DV360UpdateConnection } from '../types/dv360'
import { FacebookConfig, FacebookFinishAuth, FacebookUpdateConnection } from '../types/facebook'
import { FinishAuthRequest, FinishAuthResponse } from '../types/finishAuth'
import {
  GoogleAdwordsConfig,
  GoogleAdwordsFinishAuth,
  GoogleAdwordsUpdateConnection,
} from '../types/googleAdwords'
import { GoogleAnalyticsConfig, GoogleAnalyticsUpdateConnection } from '../types/googleAnalytics'
import { GoogleBigqueryConfig, GoogleBigqueryCreateConnection } from '../types/googleBigquery'
import { InnkeeprConfig, InnkeeprCreateConnection } from '../types/innkeepr'
import { TiktokConfig, TiktokFinishAuth, TiktokUpdateConnection } from '../types/tiktok'
import {
  WoocommerceConfig,
  WoocommerceCreateConnection,
  WoocommerceUpdateConnection,
} from '../types/woocommerce'
import { removeProtocol } from './innkeepr'

const alwaysCreateConnections = ['googleBigquery']
const hasSeparateUpdateEndpoint = ['criteo', 'dv360', 'googleAnalytics', 'innkeepr']

const createConnection = async <R extends ConnectionEntity>(
  create: (
    endpoint: string,
    data: ConnectionCreateRequest,
  ) => Promise<Result<ConnectionCreateResponse, DataError<number>>>,
  conn: AvailableConnection,
  connConfig: ConnectionConfig,
): Promise<Result<R, DataError<number>>> => {
  let content = null

  switch (conn.name) {
    case 'googleBigquery':
      {
        const config = connConfig as GoogleBigqueryConfig
        content = {
          project: config.project,
          location: config.location,
          properties: config.propertyId.split(';').filter(Boolean),
        } satisfies GoogleBigqueryCreateConnection
      }
      break
    case 'innkeepr':
      {
        const config = connConfig as InnkeeprConfig
        const trackingUrl = removeProtocol(config.trackingUrl || 'cdn.innkeepr.ai')
        const loadBalancerUrl = config.trackingUrl || 'cdn.innkeepr.ai'

        content = {
          loadBalancerUrl,
          trackingUrl,
        } satisfies InnkeeprCreateConnection
      }
      break
    case 'woocommerce':
      {
        const config = connConfig as WoocommerceConfig
        content = {
          consumerKey: config.apiKey,
          secret: config.apiSecret,
          shopBaseUrl: config.shopbaseUrl,
        } satisfies WoocommerceCreateConnection
      }
      break
  }

  if (content) {
    const result = await create(`/connections/${conn.name}/init`, {
      content,
    })
    if (result.ok) {
      const { _id: id, ...connEntity } = result.val.data
      return Ok({
        ...connEntity,
        id,
      } as R)
    }
    return result
  }

  return Promise.resolve(Err(new DataError(404, 'to be implemented')))
}

const finishAuth = async <R extends ConnectionEntity>(
  create: (
    endpoint: string,
    data: FinishAuthRequest,
  ) => Promise<Result<FinishAuthResponse, DataError<number>>>,
  connection: AvailableOrInstalledConnection,
  connectionConfig: ConnectionConfig,
  authorizationCode?: string,
  redirectUri?: string,
): Promise<Result<R, DataError<number>>> => {
  let content = null

  switch (connection.name) {
    case 'amazon':
      {
        // amazon expects the code flat, the amc instance is selected afterwards
        if (authorizationCode && redirectUri) {
          content = {
            code: authorizationCode,
            redirectUri,
          } satisfies AmazonFinishAuth
        }
      }
      break
    case 'criteo':
      {
        if (authorizationCode) {
          content = {
            data: {
              authCode: authorizationCode,
            },
            callbackUrl: redirectUri,
          } satisfies CriteoFinishAuth
        }
      }
      break
    case 'dv360':
      {
        if (authorizationCode) {
          content = {
            data: {
              authCode: authorizationCode,
            },
            callbackUrl: redirectUri,
          } satisfies DV360FinishAuth
        }
      }
      break
    case 'facebook':
      {
        if (authorizationCode) {
          content = {
            data: {
              authCode: authorizationCode,
            },
            callbackUrl: redirectUri,
          } satisfies FacebookFinishAuth
        }
      }
      break
    case 'googleAdwords':
      {
        if (authorizationCode) {
          content = {
            data: {
              authCode: authorizationCode,
            },
            callbackUrl: redirectUri,
          } satisfies GoogleAdwordsFinishAuth
        }
      }
      break
    case 'googleAnalytics':
      {
        if (authorizationCode) {
          content = {
            data: {
              authCode: authorizationCode,
            },
            callbackUrl: redirectUri,
          } satisfies GoogleAdwordsFinishAuth
        }
      }
      break
    case 'tiktok':
      {
        if (authorizationCode) {
          const config = connectionConfig as TiktokConfig
          content = {
            data: {
              advertiserIds: config.adAccountIds.split(';').filter(Boolean),
              authCode: authorizationCode,
              urlTrackingParam: config.adId,
            },
            callbackUrl: redirectUri,
          } satisfies TiktokFinishAuth
        }
      }
      break
  }

  if (content) {
    const result = await create(`/connections/${connection.name}/oauth/put`, {
      content,
    })
    return result.ok ? Ok(result.val.data as R) : result
  }

  return Promise.resolve(Err(new DataError(404, 'to be implemented')))
}

const updateConnection = async <R extends ConnectionEntity>(
  create: (
    endpoint: string,
    content: ConnectionUpdateRequest,
  ) => Promise<Result<ConnectionUpdateResponse, DataError<number>>>,
  connection: InstalledConnection,
  connectionConfig: ConnectionConfig,
): Promise<Result<R, DataError<number>>> => {
  let content = null

  switch (connection.name) {
    case 'amazon':
      {
        const config = connectionConfig as AmazonConfig
        content = {
          ...connection,
          config: {
            amcInstanceId: config.amcInstanceId,
          },
        } satisfies AmazonUpdateConnection
      }
      break
    case 'criteo':
      {
        const config = connectionConfig as CriteoConfig
        content = {
          ...connection,
          options: {
            ...connection.options,
            urlTrackingParam: config.adSetId,
          },
          config: {
            advertisers: config.advertisers,
            gumCallerId: config.gumCallerId,
          },
        } satisfies CriteoUpdateConnection
      }
      break
    case 'dv360':
      {
        const config = connectionConfig as DV360Config
        content = {
          ...connection,
          options: {
            ...connection.options,
            urlTrackingParam: config.adGroupId,
          },
          config: {
            advertisers: config.advertisers,
          },
        } satisfies DV360UpdateConnection
      }
      break
    case 'facebook':
      {
        const config = connectionConfig as FacebookConfig
        content = {
          ...connection,
          options: {
            ...connection.options,
            urlTrackingParam: config.adId,
          },
          config: {
            adAccounts: config.accounts || [],
            pixelId: config.pixelId,
          },
        } satisfies FacebookUpdateConnection
      }
      break
    case 'googleAdwords':
      {
        const config = connectionConfig as GoogleAdwordsConfig
        content = {
          ...connection,
          config: {
            adAccounts: config.accounts,
          },
          options: {
            ...connection.options,
            urlCampaignParam: config.campaignId,
          },
        } satisfies GoogleAdwordsUpdateConnection
      }
      break
    case 'googleAnalytics':
      {
        const config = connectionConfig as GoogleAnalyticsConfig
        content = {
          config: {
            trackingCredentials: {
              id: config.propertyId,
              measurementId: config.measurementId,
              secret: config.apiSecret,
            },
          },
        } satisfies GoogleAnalyticsUpdateConnection
      }
      break
    case 'innkeepr':
      {
        content = {
          ...connection,
          status: 'active',
        } satisfies InstalledConnection
      }
      break
    case 'tiktok':
      {
        const config = connectionConfig as TiktokConfig
        content = {
          ...connection,
          options: {
            ...connection.options,
            urlTrackingParam: config.adId,
          },
        } satisfies TiktokUpdateConnection
      }
      break
    case 'woocommerce':
      {
        const config = connectionConfig as WoocommerceConfig
        content = {
          ...connection,
          config: {
            consumerKey: config.apiKey,
            secret: config.apiSecret,
            shopBaseUrl: config.shopbaseUrl,
          },
        } satisfies WoocommerceUpdateConnection
      }
      break
  }

  if (content) {
    const result = await create(
      `/connections/${hasSeparateUpdateEndpoint.includes(connection.name) ? `${connection.name}/update` : 'store'}`,
      {
        content,
      },
    )
    return result.ok ? Ok(result.val.data as R) : result
  }

  return Promise.resolve(Err(new DataError(404, 'to be implemented')))
}

export const saveConnection = async <R extends ConnectionEntity>(
  create: <T, R>(endpoint: string, data: T) => Promise<Result<R, DataError<number>>>,
  connection: AvailableOrInstalledConnection,
  connectionConfig: ConnectionConfig,
  authorizationCode?: string,
  redirectUri?: string,
): Promise<Result<R, DataError<number>>> =>
  authorizationCode
    ? finishAuth(create, connection, connectionConfig, authorizationCode, redirectUri)
    : isInstalledConnection(connection) && !alwaysCreateConnections.includes(connection.name)
      ? updateConnection(create, connection, connectionConfig)
      : createConnection(create, connection, connectionConfig)
